import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ExecutionEnvironment from 'exenv';
import { connect } from 'react-redux';
import { parse } from 'query-string';

import { SEARCH_COLOR_SWATCHES_COOKIE } from 'constants/cookies';
import marketplace from 'cfg/marketplace.json';
// constants & regex
import {
  NON_SEARCH_PREFIXES,
  PRODUCT_ASIN,
  SEO_URL_RE,
  SLASH_SEARCH_RE,
  ZSO_URL_RE,
  ZSO_URL_WITH_FILTERS_RE
} from 'common/regex';
// actions
import { setError } from 'actions/errors';
import { pageTypeChange } from 'actions/common';
import { setHFSearchTerm } from 'actions/headerfooter';
import { redirectWithAppRoot } from 'actions/redirect';
import { sessionExpiration, setAndStoreCookie } from 'actions/session';
import { setOosMessaging } from 'actions/products';
import { getHeartCounts, getHearts } from 'actions/hearts';
import { fetchFromSearch, fetchFromZso, processReceivedSearchResponse } from 'actions/fancyUrls';
import {
  clearInlineRecos,
  fetchSearchInlineRecos,
  toggleFacetsContainer
} from 'actions/search';
import { fetchSponsoredProducts, fetchSymphonySearchComponents } from 'store/ducks/search/actions';
import NoSearchResults from 'components/search/NoSearchResults';
// resources
import { productBundle } from 'apis/cloudcatalog';
import { logDebug } from 'middleware/logger';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { FetchError, fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import ProductUtils from 'helpers/ProductUtils';
import { isDesktop } from 'helpers/ClientUtils';
import { stripAppRoot } from 'history/AppRootUtils';
import {
  isPrettySearchOrSlashSearchPath,
  locationLooksLikeNonNullQueryStringSearch,
  lookLikeSameSearchUrls,
  makeSearchUrl,
  normalizeSearchLocation,
  searchUrlMatchesFilters,
  shouldSearchServiceBeCalled
} from 'helpers/SearchUtils';

// marketplace
const {
  search: { hasInfinitePagination, showHorizontalInlineSearchRecos, oosMessaging },
  checkout: { allowMoveToFavorites },
  hasAds,
  hasBannerAds
} = marketplace;

export class SearchLogic extends Component {
  static build404ErrorMessage = () => setError('404 not found', new Error('Not a Search URL.'), 404);

  static fetchDataOnServer(store, location, params, { zsoFetch, searchFetch, redirect, asinFetch } = {}) {
    // default params do not work in static functions?
    searchFetch = searchFetch || fetchFromSearch;
    zsoFetch = zsoFetch || fetchFromZso;
    redirect = redirect || redirectWithAppRoot;
    asinFetch = asinFetch || productBundle;
    const qs = location.pathname === '/search' ? parse(location.search) : {};
    const potentialAsin = (params.seoName || qs.term || '').toUpperCase();
    if (ZSO_URL_RE.test(location.pathname)) {
      if (NON_SEARCH_PREFIXES.test(location.pathname)) {
        return store.dispatch(SearchLogic.build404ErrorMessage());
      } else {
        return store.dispatch(zsoFetch({ location, keepParams: true }));
      }
    } else if (PRODUCT_ASIN.test(potentialAsin)) {
      const state = store.getState();
      const { environmentConfig: { api: { cloudcatalog: cloudcatalogInfo } } } = state;
      return asinFetch(cloudcatalogInfo, { asin: potentialAsin })
        .then(fetchErrorMiddleware)
        .then(response => {
          const productReceived = response.statusCode === '200' && response.product.length;
          if (productReceived) {
            const productUrl = ProductUtils.getProductUrlFromAsin(response.product[0], potentialAsin);
            if (productUrl) {
              return store.dispatch(redirect(productUrl));
            }
          }
          throw new FetchError(location.pathname, response.statusCode, `Unexpected CloudCatalogAPI response for ASIN: ${potentialAsin}`);
        })
        .catch(() => {
          if (isPrettySearchOrSlashSearchPath(location.pathname)) {
            return store.dispatch(searchFetch({ location: normalizeSearchLocation(location), keepParams: true }));
          } else {
            return store.dispatch(SearchLogic.build404ErrorMessage());
          }
        });
    } else if (isPrettySearchOrSlashSearchPath(location.pathname)) {
      return store.dispatch(searchFetch({ location: normalizeSearchLocation(location), keepParams: true, isSearchHappeningServerSide: true }));
    } else {
      return store.dispatch(SearchLogic.build404ErrorMessage());
    }
  }

  static afterFetchDataOnServer({ dispatch, getState, doProcessReceivedSearchResponse = processReceivedSearchResponse }) {
    const { products: { deferredSearchResponse } } = getState();
    if (!deferredSearchResponse) {
      return;
    }
    const { response, parsedParams, completeUrl } = deferredSearchResponse;
    doProcessReceivedSearchResponse(response, dispatch, getState, parsedParams, completeUrl);
  }

  componentDidMount() {
    const {
      location,
      setHFSearchTerm,
      setLayoutOption,
      filters,
      fetchSearchInlineRecos,
      clearInlineRecos,
      setOosMessaging,
      getHearts,
      pageTypeChange,
      fetchSponsoredProducts,
      fetchSymphonySearchComponents,
      getHeartCounts,
      filters: { executedSearchUrl: filtersExecutedSearchUrl, term },
      products: { executedSearchUrl, list, inlineRecos, isBlacklisted, msftResults }, params: { seoName }
    } = this.props;

    const hasProducts = this.hasProductResults();

    if (hasAds && term) {
      fetchSponsoredProducts();
    }

    if (hasBannerAds && term) {
      fetchSymphonySearchComponents();
    }

    pageTypeChange('search');

    const urlSearchPageParam = this.getPageFromUrlSearch(location.search);

    // if they we redirected to search due to an oos item
    if (location.query && location.query.oosRedirected) {
      setOosMessaging(oosMessaging);
      return false;
    }

    if (!isBlacklisted && shouldSearchServiceBeCalled(location, seoName, executedSearchUrl)) {
      this.requestSearchResultsForLocation({ location, isFresh: true });
    }

    if (locationLooksLikeNonNullQueryStringSearch(location) && executedSearchUrl) {
      this.updateUrl(executedSearchUrl);
    }

    if (setLayoutOption) {
      setLayoutOption('showBackToTop');
    }

    if (!this.loaded && filtersExecutedSearchUrl) {
      this.loaded = true;
    }

    if (filters.originalTerm) {
      setHFSearchTerm(filters.originalTerm);// For Marty HF
    }

    if (this.shouldFetchSearchInlineRecos() && filters.originalTerm && hasProducts && !urlSearchPageParam) {
      fetchSearchInlineRecos(filters.originalTerm);
    } else if (inlineRecos) {
      clearInlineRecos();
    }

    if (allowMoveToFavorites) {
      // Get hearting list
      getHearts();

      // Get hearting count
      if (hasProducts) {
        // Currently, cloudlist API has a cap of 100 products. If that's increased, we can do something like getHeartCounts([...list, ...msftResults]) to consolidate the calls
        getHeartCounts(list);
        getHeartCounts(msftResults);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      filters: prevFilters,
      filters: { term: prevTerm, applySavedFilters: prevSavedFilters },
      location,
      setHFSearchTerm,
      fetchSearchInlineRecos,
      clearInlineRecos,
      products,
      setOosMessaging,
      getHeartCounts
    } = prevProps;
    const {
      filters,
      filters: { staleProducts: stale, term, originalTerm, executedSearchUrl: filtersExecutedSearchUrl, applySavedFilters },
      location: nextLocation,
      products: {
        executedSearchUrl,
        requestedUrl,
        inlineRecos,
        list
      },
      fetchSymphonySearchComponents,
      makeScrollhandler
    } = this.props;

    const currentUrl = location.pathname + location.search;
    const nextUrl = nextLocation.pathname + nextLocation.search;
    const nextUrlSearchPageParam = this.getPageFromUrlSearch(nextLocation.search);
    const isNewUrl = nextUrl !== currentUrl;
    const nextUrlWithoutAppRoot = stripAppRoot(nextUrl);
    const isCurrentUrlMartyPrefixed = currentUrl.indexOf('/marty/') === 0;
    const isNewHorizRecos = inlineRecos && inlineRecos !== products.inlineRecos;
    const isNewProducts = list && list.length && list !== products.list;
    const isNewOriginalTerm = this.props.filters.originalTerm !== originalTerm;
    logDebug(nextUrl, currentUrl, requestedUrl, executedSearchUrl);

    if (isNewOriginalTerm || (!this.loaded && filtersExecutedSearchUrl)) {
      this.loaded = true;
    }

    if (stale && !searchUrlMatchesFilters(requestedUrl, filters)) {
      // facet/sort/page/priceRange update
      // not yet requested
      logDebug('stale products due to facet update');
      const [pathname, search] = makeSearchUrl(filters).split(/\?/, 2);
      const mockLocation = { pathname, search: search ? `?${search}` : '' };
      this.requestSearchResultsForLocation({ location: mockLocation });
      setOosMessaging(null);
      this.mobileScrollToTop();
    } else if (isNewUrl && !ZSO_URL_RE.test(nextUrl) && !SEO_URL_RE.test(nextUrl) && requestedUrl !== nextUrlWithoutAppRoot) {
      // the url is changing to either a typed search url or another non-zso url,
      // but we haven't requested it yet.
      logDebug('stale typed or enhanced autcomplete search');
      this.requestSearchResultsForLocation({ location: nextLocation });
    } else if (isNewUrl && (ZSO_URL_RE.test(nextUrl) || SEO_URL_RE.test(nextUrl)) && executedSearchUrl !== nextUrlWithoutAppRoot) {
      // url changed to a zso or fancy typed search, which is not updating theurl to be the canonical.
      // this must be the back button, or a new search link on the page (i.e header nav).
      // this is a case where we will take the url and update search results based on it.
      logDebug('to zso/seo');
      this.requestSearchResultsForLocation({ location: nextLocation });

      this.mobileScrollToTop();
    } else if (executedSearchUrl && executedSearchUrl !== nextUrlWithoutAppRoot) {
      // we haven't updated the display url yet to match facets, but we have
      // one to update to, so update the display url.
      if (ZSO_URL_RE.test(executedSearchUrl)) {
        logDebug('canonicalizable zso');
        if ((nextUrlWithoutAppRoot === requestedUrl && requestedUrl !== executedSearchUrl) || lookLikeSameSearchUrls(executedSearchUrl, nextUrlWithoutAppRoot)) {
          // we submitted either a zso, a naked search url, or a brand url to search, and what was actually executed was slightly different
          // We don't want to push onto the stack since the two URLs are the same (with different history breadcrumbs)
          this.updateUrl(executedSearchUrl);
        } else {
          const forcePushUrl = isCurrentUrlMartyPrefixed ? `/marty${executedSearchUrl}` : executedSearchUrl;
          // if infinite scrolling, we don't want to update the url
          if (hasInfinitePagination && forcePushUrl.includes('p=')) {
            return;
          }
          // this is the executed url for a zso search for which we have not
          // updated the url at all yet. So push on a new url
          this.context.router.forceBrowserPush(forcePushUrl);
        }
      } else if (executedSearchUrl !== location.pathname) {
        // respect the oosRedirected flag and do not get rid of it, it won't appear in the backend since it's a front-end only flag.

        // this is a typed search going from /search?term=foo to /foo
        // so just replace the url since a history entry was already added
        // for this url
        logDebug('canonicalizable typed search');
        this.updateUrl(executedSearchUrl);
      }
    }

    if (applySavedFilters !== prevSavedFilters) {
      this.requestSearchResultsForLocation({ location: nextLocation });
    }

    if (prevFilters.originalTerm !== filters.originalTerm) {
      // keep the header term in sync with the searched for term.
      setHFSearchTerm(filters.originalTerm);// For Marty HF
    }

    if (this.shouldFetchSearchInlineRecos(nextLocation.pathname) && isNewUrl && !nextUrlSearchPageParam) {
      fetchSearchInlineRecos(filters.originalTerm);
    } else if (inlineRecos && isNewUrl) {
      clearInlineRecos();
    }

    // Get hearting count if applicable
    if (isNewProducts) {
      getHeartCounts(list);
    }
    if (isNewHorizRecos) {
      getHeartCounts(inlineRecos.recos);
    }

    if (hasBannerAds && term && prevTerm !== term) {
      fetchSymphonySearchComponents();
    }

    // Watch scrolling for scrollToTop
    const hasProducts = this.hasProductResults();
    if (!this.scrollHandlerLoaded && makeScrollhandler && hasProducts) {
      this.scrollHandlerLoaded = true;
      makeScrollhandler();
    }
  }

  componentWillUnmount() {
    const { setLayoutOption } = this.props;

    if (setLayoutOption) {
      setLayoutOption('showBackToTop', 0);
    }

    this.loaded = false;
  }

  // Search loaded
  loaded = false;
  scrollHandlerLoaded = false;

  requestSearchResultsForLocation = ({ location, isFresh, bypassCache, shouldAppendResults }) => {
    const { fetchFromSearch, fetchFromZso } = this.props;
    if (ZSO_URL_RE.test(location.pathname)) {
      return fetchFromZso({ location, isFresh, bypassCache, shouldAppendResults });
    } else {
      return fetchFromSearch({ location: normalizeSearchLocation(location), isFresh, bypassCache, shouldAppendResults });
    }
  };

  updateUrl = url => {
    this.context.router.replacePreserveAppRoot(url);
  };

  mobileScrollToTop = () => !isDesktop() && this.scrollToTop();

  getPageFromUrlSearch = search => {
    const nextUrlSearchParsed = parse(search);
    return nextUrlSearchParsed && nextUrlSearchParsed.p ? parseInt(nextUrlSearchParsed.p, 10) : null;
  };

  scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  shouldFetchSearchInlineRecos = (pathname = this.props.location.pathname) => {
    const { filters: { page } } = this.props;
    const doesNotHaveZsoFilters = !ZSO_URL_WITH_FILTERS_RE.test(pathname);
    const doesNotHaveSlashFilters = !SLASH_SEARCH_RE.test(pathname);
    return showHorizontalInlineSearchRecos && doesNotHaveZsoFilters && doesNotHaveSlashFilters && ExecutionEnvironment.canUseDOM && page === 0;
  };

  hasProductResults = () => {
    const { products: { allProductsCount, list }, hydraCrossSiteSelling } = this.props;
    return hydraCrossSiteSelling ? allProductsCount > 0 : !!list?.length;
  };

  shouldShowNoResults = () => {
    const { products } = this.props;
    return !!products.requestedUrl && !products.isLoading && !this.hasProductResults();
  };

  handleColorSwatchToggle = ({ target: { checked } }) => {
    const { location, setAndStoreCookie } = this.props;
    const expiration = sessionExpiration();
    const newCookie = checked ? 'on' : 'off';
    setAndStoreCookie(SEARCH_COLOR_SWATCHES_COOKIE, newCookie, expiration);
    trackEvent('TE_SEARCH_RELATED_STYLES_TOGGLE', `${checked}`);
    this.requestSearchResultsForLocation({ location, bypassCache: true });
  };

  render() {
    const { handleColorSwatchToggle, hasProductResults, requestSearchResultsForLocation, shouldShowNoResults } = this;
    const { sponsoredProducts, isCustomer } = this.props;
    return (
     <>
       {this.shouldShowNoResults() && <NoSearchResults />}
       {this.props.children({
         handleColorSwatchToggle,
         hasProductResults,
         requestSearchResultsForLocation,
         shouldShowNoResults,
         sponsoredProducts,
         isCustomer
       })}
     </>
    );
  }
}

SearchLogic.defaultProps = {
  trackEvent,
  trackLegacyEvent
};

SearchLogic.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func.isRequired
};

SearchLogic.propTypes = {
  products: PropTypes.object.isRequired,
  filters: PropTypes.object,
  facets: PropTypes.object
};

function mapStateToProps(state) {
  const { cookies, products, facets, filters, isFacetsVisible, search: { sponsoredProducts } } = state;
  const isCustomer = state.cookies['x-main'];

  return {
    cookies,
    products,
    facets,
    filters,
    isFacetsVisible,
    isCustomer,
    sponsoredProducts
  };
}

export const mapDispatchToProps = {
  redirectWithAppRoot,
  toggleFacetsContainer,
  fetchSearchInlineRecos,
  clearInlineRecos,
  setOosMessaging,
  getHearts,
  pageTypeChange,
  getHeartCounts,
  setHFSearchTerm,
  setAndStoreCookie,
  sessionExpiration,
  fetchFromZso,
  fetchFromSearch,
  fetchSponsoredProducts,
  fetchSymphonySearchComponents
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchLogic);
